import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Filter,
  RefreshCw,
  Plus,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { cn } from '../../utils/cn';
import api from '../../services/api';
import DashboardModuleShell from '../../components/DashboardModuleShell';
import { formatDateTime } from '../../utils/formatDate';

const STATUS_CONFIG = {
  active:    { label: 'Active',    color: 'bg-[#EBF5FB] text-[#0176D3]',  icon: Clock },
  completed: { label: 'Completed', color: 'bg-[#E1F2E8] text-[#04844B]',  icon: CheckCircle },
  expired:   { label: 'Expired',   color: 'bg-[#F4F6F9] text-[#706E6B]',  icon: AlertCircle },
  failed:    { label: 'Failed',    color: 'bg-[#FEE8E8] text-[#EA001E]',   icon: XCircle },
};

const formatDate = (dateStr) => (dateStr ? formatDateTime(dateStr) : '-');

const truncateJson = (data, maxLen = 80) => {
  if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) return '{}';
  const str = JSON.stringify(data);
  return str.length > maxLen ? str.slice(0, maxLen) + '…' : str;
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.expired;
  const Icon = cfg.icon;
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest', cfg.color)}>
      <Icon size={10} />
      {cfg.label}
    </span>
  );
};

const WhatsAppFlowSessionsPage = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [flowIdFilter, setFlowIdFilter] = useState('all');
  const [selectedSession, setSelectedSession] = useState(null);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (flowIdFilter !== 'all') params.flowId = flowIdFilter;
      const res = await api.get('/whatsapp-flows/sessions', { params });
      setSessions(res.data || []);
    } catch (err) {
      console.error('Failed to fetch flow sessions', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, flowIdFilter]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Derive unique flowIds from loaded sessions for filter dropdown
  const uniqueFlowIds = [...new Set(sessions.map(s => s.metaFlowId || s.flowId).filter(Boolean))];

  const stats = [
    { label: 'Total Sessions',    value: sessions.length,                                            icon: Activity,     color: 'text-[#0176D3]' },
    { label: 'Completed',         value: sessions.filter(s => s.status === 'completed').length,      icon: CheckCircle,  color: 'text-[#04844B]' },
    { label: 'Active',            value: sessions.filter(s => s.status === 'active').length,         icon: Clock,        color: 'text-[#0176D3]' },
    { label: 'Failed / Expired',  value: sessions.filter(s => ['failed','expired'].includes(s.status)).length, icon: XCircle, color: 'text-[#EA001E]' },
  ];

  return (
    <DashboardModuleShell
      title="Flow Sessions"
      subtitle="Submitted form data from WhatsApp Flow interactions"
      icon={Activity}
      stats={stats}
      actions={
        <div className="flex items-center gap-3">
          <Button variant="outline" icon={RefreshCw} onClick={fetchSessions} loading={loading}>
            Refresh
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Filter Bar */}
        <Card className="p-4 border-[#D8DDE6] shadow-sm">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-[#706E6B]">
              <Filter size={14} />
              <span className="text-[11px] font-bold uppercase tracking-widest">Filters</span>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <label className="text-[10px] font-bold text-[#706E6B]">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg text-[12px] font-bold outline-none cursor-pointer hover:bg-white transition-all focus:border-[#0176D3]"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="expired">Expired</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            {/* Flow ID Filter */}
            <div className="flex items-center gap-2">
              <label className="text-[10px] font-bold text-[#706E6B]">Flow</label>
              <select
                value={flowIdFilter}
                onChange={(e) => setFlowIdFilter(e.target.value)}
                className="px-4 py-2 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg text-[12px] font-bold outline-none cursor-pointer hover:bg-white transition-all focus:border-[#0176D3]"
              >
                <option value="all">All Flows</option>
                {uniqueFlowIds.map(id => (
                  <option key={id} value={id}>{id}</option>
                ))}
              </select>
            </div>

            <div className="ml-auto">
              <Badge variant="blue">{sessions.length} Sessions</Badge>
            </div>
          </div>
        </Card>

        {/* Sessions Table */}
        <Card className="p-0 overflow-hidden border-[#D8DDE6] shadow-sm">
          <div className="px-6 py-4 bg-[#F4F6F9] border-b border-[#D8DDE6] flex items-center justify-between">
            <h3 className="text-[12px] font-bold text-[#706E6B] uppercase tracking-widest">Session Ledger</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-white border-b border-[#D8DDE6]">
                  <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">User Phone</th>
                  <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Flow ID</th>
                  <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Status</th>
                  <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Collected Data</th>
                  <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Created At</th>
                  <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Completed At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF1F6]">
                {loading && sessions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-24 text-center">
                      <RefreshCw className="w-10 h-10 animate-spin text-[#0176D3] mx-auto mb-4" />
                      <p className="text-[12px] font-bold text-[#706E6B] uppercase tracking-widest">Loading Sessions…</p>
                    </td>
                  </tr>
                ) : sessions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-24 text-center px-6">
                      <div className="w-20 h-20 rounded-lg bg-[#F4F6F9] border border-[#D8DDE6] flex items-center justify-center mb-6 mx-auto">
                        <Activity className="w-10 h-10 text-[#706E6B]" />
                      </div>
                      <h3 className="text-base font-bold text-[#080707] mb-2 tracking-tight">No Sessions Found</h3>
                      <p className="text-[13px] text-[#706E6B] mb-4 max-w-sm mx-auto font-medium leading-relaxed">
                        No WhatsApp Flow sessions match your current filters. Try adjusting the status or flow filter.
                      </p>
                    </td>
                  </tr>
                ) : (
                  sessions.map((session) => (
                    <tr
                      key={session._id}
                      onClick={() => setSelectedSession(session)}
                      className="group hover:bg-[#F4F6F9]/50 transition-all duration-200 cursor-pointer"
                    >
                      {/* User Phone */}
                      <td className="px-6 py-5">
                        <span className="text-[13px] font-bold text-[#080707] group-hover:text-[#0176D3] transition-colors font-mono">
                          {session.userPhone || session.phoneNumber || '-'}
                        </span>
                      </td>

                      {/* Flow ID */}
                      <td className="px-6 py-5">
                        <span className="text-[11px] font-bold text-[#706E6B] font-mono bg-[#F4F6F9] border border-[#D8DDE6] px-2 py-1 rounded-lg">
                          {session.metaFlowId || session.flowId || '-'}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-5">
                        <StatusBadge status={session.status} />
                      </td>

                      {/* Collected Data */}
                      <td className="px-6 py-5 max-w-xs">
                        <code className="text-[11px] text-[#706E6B] font-mono bg-[#F4F6F9] px-2 py-1 rounded block truncate">
                          {truncateJson(session.collectedData)}
                        </code>
                        <span className="text-[9px] text-[#0176D3] font-bold uppercase tracking-widest mt-1 block opacity-0 group-hover:opacity-100 transition-opacity">
                          Click to expand
                        </span>
                      </td>

                      {/* Created At */}
                      <td className="px-6 py-5">
                        <span className="text-[12px] text-[#706E6B] font-medium">{formatDate(session.createdAt)}</span>
                      </td>

                      {/* Completed At */}
                      <td className="px-6 py-5">
                        <span className="text-[12px] text-[#706E6B] font-medium">{formatDate(session.completedAt)}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Detail Modal */}
      {selectedSession && (
        <div
          className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/40 sm:p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setSelectedSession(null); }}
        >
          <div className="bg-white w-full max-w-2xl rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] flex flex-col overflow-y-auto max-h-[100dvh]">
            {/* Modal Header */}
            <div className="px-4 sm:px-6 py-3 flex items-center justify-between border-b border-[#E5E5E5] shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#0176D3] text-white flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.16)] shadow-[#0176D3]/30">
                  <Activity size={20} />
                </div>
                <div>
                  <h3 className="text-[16px] font-bold text-[#2E2E2E]">Session Details</h3>
                  <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-widest mt-0.5">
                    {selectedSession.userPhone || selectedSession.phoneNumber || 'Unknown User'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedSession(null)}
                className="w-10 h-10 flex items-center justify-center hover:bg-white rounded-full transition-all border border-transparent hover:border-[#D8DDE6] text-[#706E6B]"
              >
                <Plus className="w-5 h-5 rotate-45" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 p-8 overflow-y-auto no-scrollbar space-y-6">
              {/* Meta Fields */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Session ID',    value: selectedSession._id },
                  { label: 'Flow ID',       value: selectedSession.metaFlowId || selectedSession.flowId || '-' },
                  { label: 'User Phone',    value: selectedSession.userPhone || selectedSession.phoneNumber || '-' },
                  { label: 'Status',        value: null, node: <StatusBadge status={selectedSession.status} /> },
                  { label: 'Created At',    value: formatDate(selectedSession.createdAt) },
                  { label: 'Completed At',  value: formatDate(selectedSession.completedAt) },
                  ...(selectedSession.expiresAt ? [{ label: 'Expires At', value: formatDate(selectedSession.expiresAt) }] : []),
                ].map(({ label, value, node }) => (
                  <div key={label} className="bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg p-4">
                    <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-widest mb-1">{label}</p>
                    {node || (
                      <p className="text-[13px] font-bold text-[#080707] break-all font-mono">{value}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Collected Data */}
              <div>
                <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Activity size={10} className="text-[#0176D3]" />
                  Collected Data
                </p>
                <div className="bg-[#080707] rounded-lg p-6 overflow-x-auto">
                  <pre className="text-[12px] text-[#04844B] font-mono leading-relaxed whitespace-pre-wrap break-all">
                    {JSON.stringify(selectedSession.collectedData || {}, null, 2)}
                  </pre>
                </div>
              </div>

              {/* Extra fields catch-all */}
              {selectedSession.currentScreen && (
                <div className="bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg p-4">
                  <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-widest mb-1">Current Screen</p>
                  <p className="text-[13px] font-bold text-[#080707] font-mono">{selectedSession.currentScreen}</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-8 py-5 border-t border-[#EEF1F6] bg-[#F4F6F9]/30 flex justify-end shrink-0">
              <Button variant="outline" onClick={() => setSelectedSession(null)} className="h-12 px-8">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardModuleShell>
  );
};

export default WhatsAppFlowSessionsPage;
